import {
  CCol,
  CDropdown,
  CDropdownItem,
  CDropdownMenu,
  CDropdownToggle,
  CInputCheckbox,
  CInputFile,
  CInputRadio,
  CLabel,
  CRow,
  CTextarea
} from "@coreui/react"
import downArrow from "assets/arrow_drop_down.svg"
import classNames from "classnames"
import ToasterProvider from "components/common/Context/ToasterContext"
import { withErrorBoundary } from "components/common/error"
import { LoadingSpinnerOverlay } from "components/common/loading"
import Tooltip from "components/common/tooltip/Tooltip"
import { feedbackDrawerText } from "constants/StringConstants"
import { FormActions } from "core/modules/service/components/serviceRequest"
import { postFeedback, uploadFeedbackImage } from "extensions/usoc/api/cp"
import jwtDecode from "jwt-decode"
import React, { useContext, useState } from "react"
import { useSelector } from "react-redux"
import { useExtension } from "utils"
import { useUserAuth } from "utils/hooks"

import { useTheme } from "../Context/ThemeContext"
import { Drawer } from "../drawer"
import { ConfirmModal } from "../modal"
import { Rating } from "../rating"
import CMSTypography, {
  fontSizes,
  fontWeights,
  StandardTypographyTypes
} from "../typography/CMSTypography"

const checkIfFileSupported = (file = {}) => {
  const extension = file?.name?.split(".").slice(-1).pop()
  if (extension) {
    return ["jpeg", "png", "zip", "pdf"].includes(extension)
  }
  return false
}

export default withErrorBoundary(function ({
  isOpen,
  setSidebarIsOpen = () => { }
}) {
  const auth = useUserAuth()
  const token = auth.token
  let decodedToken
  try {
    decodedToken = jwtDecode(token)
  } catch (error) {
    decodedToken = {}
  }

  const projectID = useSelector((state) => {
    return state.settings.currentProject
  })

  const userEmail = useSelector((state) => {
    return state.user.email
  })

  const extension = useExtension()
  const theme = useTheme()
  const host = extension?.config?.env?.clientApiGateway?.URL

  const [isDrawerMinimized, setIsDrawerMinimized] = useState(false)
  const [isWarningVisible, setIsWarningVisible] = useState(false)
  const [detailedFeedback, setDetailedFeedback] = useState(false)
  const [overallRating, setOverallRating] = useState(0)
  const [comment, setComment] = useState("")
  const [urgency, setUrgency] = useState("")
  const [isUrgencyTouched, setIsUrgencyTouched] = useState(false)
  const [description, setDescription] = useState("")

  const [isAnonymous, setIsAnonymous] = useState(false)
  const [uploadFile, setUploadFile] = useState()

  const [isDescriptionTouched, setIsDescriptionTouched] = useState(false)
  const [selectedType, setSelectedType] = useState("")
  const [isTypeTouched, setIsTypeTouched] = useState(false)

  const urgencies = ["Low", "Medium", "High"]
  const types = ["Bug", "New Feature", "Data Quality"]

  const [isLoading, setIsLoading] = useState(false)
  const enqueueToast = useContext(ToasterProvider)

  const handleClose = () => {
    setOverallRating(0)
    setComment("")
    setUrgency("Low")
    setDescription("")
    setIsAnonymous(true)
    setIsDescriptionTouched(false)
    setIsTypeTouched(false)
    setIsWarningVisible(false)
    setSidebarIsOpen(false)
  }

  const postFeedbackAndFile = async (base64File) => {
    const feedbackBody = {
      user_xp: overallRating,
      comments: comment,
      urgency,
      subject: selectedType,
      description,
      anonymous: isAnonymous ? "True" : "False",
      email: userEmail,
      full_name: `${decodedToken?.given_name} ${decodedToken?.family_name}`,

      happy_cms: "Yes",
      features: 2,
      functionality: 2,
      response: "True"
    }
    const response = await postFeedback(
      { auth, host, body: JSON.stringify(feedbackBody) },
      projectID
    )
    if (response.error || !response.data) {
      setIsLoading(false)
      enqueueToast("failure", null, "Could not submit the feedback")
      return
    }
    if (!base64File) {
      setIsLoading(false)
      enqueueToast("success", null, "Feedback submitted successfully")
      handleClose()
      return
    }
    const fileName = uploadFile.name
      .split(".")
      .splice(0, uploadFile.name.split(".").length - 1)
      .join(".")
    const feedbackId = response?.data?.ID || ""
    const uploadFileResponse = await uploadFeedbackImage(
      {
        auth,
        host,
        body: JSON.stringify({ file: base64File })
      },
      projectID,
      `image_name=${fileName}&feedback_id=${feedbackId}`
    )
    setIsLoading(false)
    if (
      uploadFileResponse.error ||
      !uploadFileResponse.data ||
      uploadFileResponse.data?.success === false ||
      uploadFileResponse?.data?.toLowerCase() !== "post successful"
    ) {
      enqueueToast(
        "failure",
        null,
        "Feedback submitted but could not upload Image"
      )
    } else {
      enqueueToast("success", null, "Feedback submitted successfully")
      handleClose()
    }
  }
  /**
   * For handling feedback form submittion
   */
  const handleSubmit = async () => {
    setIsDescriptionTouched(true)
    setIsUrgencyTouched(true)
    setIsTypeTouched(true)
    if (
      detailedFeedback &&
      (!selectedType ||
        !urgency ||
        !description ||
        (uploadFile && !checkIfFileSupported(uploadFile)))
    )
      return
    setIsLoading(true)
    if (uploadFile) {
      const fileReader = new FileReader()
      fileReader.readAsDataURL(uploadFile)
      fileReader.onload = function () {
        postFeedbackAndFile(fileReader.result)
      }
      fileReader.onerror = function (error) {
        setIsLoading(false)
        console.log("Error in converting file ", error)
        enqueueToast("failure", null, "Error in converting file")
      }
    } else {
      postFeedbackAndFile()
    }
  }

  const feedbackForm = (
    <>
      <div className="service-request-container feedback-form pr-5 h-100 overflow-y-scroll feedback-background pl-4 field-label ">
        <LoadingSpinnerOverlay isLoading={isLoading} spinnerSize="8rem">
          <CRow className="ml-3 mt-3">
            <CCol md="12">
              <div className="mb-2">
                <CMSTypography type={StandardTypographyTypes.cardContent}>
                  Thank you for taking the time to share your thoughts and
                  feedback!
                </CMSTypography>
              </div>
            </CCol>
          </CRow>
          <CRow className="ml-3 mt-1">
            <CCol md="12">
              <div className="mb-2">
                <CMSTypography type={StandardTypographyTypes.cardSubHeading}>
                  Overall Satisfaction With The Portal *
                </CMSTypography>
              </div>
            </CCol>
          </CRow>
          <CRow className="ml-3">
            <CCol>
              <Rating value={overallRating} onChange={setOverallRating} />
            </CCol>
          </CRow>
          <CRow className="m-3">
            <CCol>
              <CInputCheckbox
                className="ml-0"
                id="detailed-feedback"
                data-testid="detailed-feedback"
                checked={detailedFeedback}
                onChange={() => setDetailedFeedback(!detailedFeedback)}
              />
              <CLabel
                htmlFor="detailed-feedback"
                className="cursor-pointer mt-1 ml-4 mb-2"
              >
                <CMSTypography type={StandardTypographyTypes.cardContent}>
                  Would you like to provide detailed feedback?
                </CMSTypography>
              </CLabel>
            </CCol>
          </CRow>
          <div className={`${!detailedFeedback ? "cursor-not-allowed" : ""}`}>
            <CRow className="m-3 pr-3">
              <CCol md="12">
                <div className="mb-2">
                  <CMSTypography type={StandardTypographyTypes.cardSubHeading}>
                    Urgency *
                  </CMSTypography>
                </div>
                <CDropdown id="all-folders" className="w-100">
                  <CDropdownToggle
                    className={`bg-white text-dark d-flex align-items-center justify-content-between textBox-style min-width-100 pr-3 ${isUrgencyTouched && !urgency
                      ? "invalid-border is-invalid"
                      : ""
                      }
                    ${!detailedFeedback ? "cursor-not-allowed" : ""}
                    `}
                    caret
                    disabled={isLoading || !detailedFeedback}
                  >
                    {
                      <>
                        <span className="text-ellipsis">
                          <CMSTypography
                            type={StandardTypographyTypes.cardSubHeading}
                          >
                            {urgency === "" ? "Select urgency" : urgency}
                          </CMSTypography>
                        </span>
                        <img
                          src={downArrow}
                          alt="down arrow"
                          className="iconDropdownStyle"
                        />
                      </>
                    }
                  </CDropdownToggle>
                  {isUrgencyTouched && !urgency && (
                    <div className="mb-2" data-testid={"desc-error"}>
                      <CMSTypography
                        type={StandardTypographyTypes.cardContent}
                        color={theme.danger}
                      >
                        This Field is Required
                      </CMSTypography>
                    </div>
                  )}
                  <CDropdownMenu className="pt-0 w-100 dropdown-scrollbar font-styling p-0">
                    <div className="width-max-content min-width-100">
                      {urgencies?.map((item, index) => (
                        <CDropdownItem
                          onClick={() => {
                            setUrgency(item)
                          }}
                          className="flex-row w-100 feedback-urgency-list"
                          key={`${item.key}-${index}`}
                        >
                          <div className="flex-column align-items-start">
                            <CMSTypography
                              type={StandardTypographyTypes.cardContent}
                            >
                              {item}
                            </CMSTypography>
                          </div>
                        </CDropdownItem>
                      ))}
                    </div>
                  </CDropdownMenu>
                </CDropdown>
              </CCol>
            </CRow>

            <CRow className="mt-3 ml-3 pr-3 mb-2">
              <CCol>
                <CMSTypography type={StandardTypographyTypes.cardSubHeading}>
                  Type *
                </CMSTypography>
              </CCol>
            </CRow>
            {types.map((type, index) => (
              <CRow className="ml-3" key={type}>
                <CCol md="5">
                  <CInputRadio
                    className={`mr-2 ml-1 ${!detailedFeedback
                      ? "cursor-not-allowed"
                      : "cursor-pointer"
                      }`}
                    checked={type === selectedType}
                    onChange={() => {
                      setSelectedType(type)
                      setIsTypeTouched(true)
                    }}
                    id={`type-${index}`}
                    data-testid={`type-${index}`}
                    disabled={!detailedFeedback}
                  />
                  <CLabel
                    className={`ml-4 pl-2 mt-1 mb-2 ${detailedFeedback ? "cursor-pointer" : ""
                      }`}
                    htmlFor={`type-${index}`}
                  >
                    <CMSTypography
                      fontSize={fontSizes.small}
                      fontWeight={
                        type === selectedType
                          ? fontWeights.medium
                          : fontWeights.light
                      }
                    >
                      {type}
                    </CMSTypography>
                  </CLabel>
                </CCol>
              </CRow>
            ))}

            <CRow className="ml-3">
              {isTypeTouched && !selectedType && (
                <CCol className="ml-1 mt-1 mb-2" data-testid={"desc-error"}>
                  <CMSTypography
                    type={StandardTypographyTypes.cardContent}
                    color={theme.validUrlColor}
                  >
                    This Field is Required
                  </CMSTypography>
                </CCol>
              )}
            </CRow>

            <CRow className="m-3 pr-3">
              <CCol md="12">
                <div className="mb-2">
                  <CMSTypography type={StandardTypographyTypes.cardSubHeading}>
                    Please Describe Feature Request *
                  </CMSTypography>
                </div>
                <CTextarea
                  type="text"
                  name="description"
                  data-testid={"desc-input"}
                  rows="3"
                  placeholder="Description"
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value)
                    setIsDescriptionTouched(true)
                  }}
                  className={classNames({
                    "is-invalid": isDescriptionTouched && !description,
                    "cursor-not-allowed": !detailedFeedback
                  })}
                  disabled={!detailedFeedback}
                />
                {isDescriptionTouched && !description && (
                  <div className="mb-2" data-testid={"desc-error"}>
                    <CMSTypography
                      type={StandardTypographyTypes.cardContent}
                      color={theme.danger}
                    >
                      This Field is Required
                    </CMSTypography>
                  </div>
                )}
              </CCol>
            </CRow>

            <CRow className="ml-3 pr-3">
              <CCol md="12">
                <div className="mb-2">
                  <CMSTypography type={StandardTypographyTypes.cardSubHeading}>
                    Upload Image
                  </CMSTypography>
                </div>
                <div className="mb-2">
                  <CMSTypography type={StandardTypographyTypes.cardContent}>
                    {" "}
                    Supported Format: png, jpeg and pdf .For multiple files or
                    other formats, use .zip
                  </CMSTypography>
                </div>
                <CInputFile
                  accept=".jpeg,.png,.zip,.pdf"
                  onChange={(event) => setUploadFile(event.target.files[0])}
                  className={classNames({
                    "is-invalid":
                      uploadFile && !checkIfFileSupported(uploadFile),
                    "cursor-not-allowed": !detailedFeedback
                  })}
                  disabled={!detailedFeedback}
                />
                {uploadFile && !checkIfFileSupported(uploadFile) && (
                  <div className="mb-2">
                    <CMSTypography
                      type={StandardTypographyTypes.cardContent}
                      color={theme.danger}
                    >
                      This Field only supports png, jpeg, pdf or zip files
                    </CMSTypography>
                  </div>
                )}
              </CCol>
            </CRow>

            <CRow className="ml-3 mt-3 pr-3">
              <CCol md="12 d-flex">
                <div className="mb-2">
                  <CMSTypography type={StandardTypographyTypes.cardSubHeading}>
                    {" "}
                    Would You Like To Submit The Response Anonymously?
                  </CMSTypography>
                </div>
                <Tooltip text={feedbackDrawerText} />
              </CCol>
            </CRow>

            <CRow className="ml-3 pr-3">
              <CCol md="3">
                <CInputRadio
                  className={`mr-2 ml-1 ${!detailedFeedback ? "cursor-not-allowed" : "cursor-pointer"
                    }`}
                  checked={isAnonymous}
                  onChange={() => {
                    setIsAnonymous(true)
                  }}
                  disabled={!detailedFeedback}
                  id="anonymous"
                />
                <CLabel
                  className={`field-label ml-4 pl-2 mt-1 ${isAnonymous ? "font-weight-bold" : ""
                    } ${detailedFeedback ? "cursor-pointer" : ""}`}
                  htmlFor="anonymous"
                >
                  Yes
                </CLabel>
              </CCol>
              <CCol md="3">
                <CInputRadio
                  className={`mr-2 ml-1 ${!detailedFeedback ? "cursor-not-allowed" : "cursor-pointer"
                    }`}
                  checked={!isAnonymous}
                  onChange={() => setIsAnonymous(false)}
                  disabled={!detailedFeedback}
                  id="not-anonymous"
                />
                <CLabel
                  className={`field-label ml-4 pl-2 mt-1 ${!isAnonymous ? "font-weight-bold" : ""
                    } ${detailedFeedback ? "cursor-pointer" : ""}`}
                  htmlFor="not-anonymous"
                >
                  No
                </CLabel>
              </CCol>
            </CRow>
          </div>

          {isOpen && (
            <FormActions
              onSubmit={handleSubmit}
              onCancel={() => setIsWarningVisible(true)}
              className="pr-4"
              isSubmitDisabled={overallRating === 0}
            />
          )}
        </LoadingSpinnerOverlay>
      </div>
    </>
  )

  return (
    <>
      <Drawer
        isOpen={isOpen}
        onClose={() => setIsWarningVisible(true)}
        heading="Feedback"
        minimizable
        minimized={isDrawerMinimized}
        setMinimized={setIsDrawerMinimized}
        isSmall={true}
        padding={false}
      >
        {feedbackForm}
      </Drawer>
      <ConfirmModal
        show={isWarningVisible}
        setShow={setIsWarningVisible}
        message="All unsaved data will be lost!"
        onCancel={() => setIsWarningVisible(false)}
        onOkay={handleClose}
      />
    </>
  )
})
